import { useEffect, useRef, useState } from 'react';
import { pagesPubliquesApi } from '../../api/resources';
import { CLE_PHOTO_DEPUIS_LA_PAGE } from '../../lib/lien-public';

/**
 * Retire d'une page rendue tout ce qui pourrait agir : scripts, cadres,
 * formulaires, attributs d'événement et adresses `javascript:`. La page vient
 * du service de rendu, qui échappe déjà les textes ; ceci n'en est qu'une
 * garde de plus.
 *
 * @param {Document} page page analysée
 */
export function nettoie(page) {
  page
    .querySelectorAll('script, iframe, object, embed, form, link, meta, base')
    .forEach((n) => n.remove());
  page.querySelectorAll('*').forEach((n) => {
    [...n.attributes].forEach((a) => {
      const nom = a.name.toLowerCase();
      const valeur = String(a.value || '')
        .trim()
        .toLowerCase();
      if (nom.startsWith('on') || valeur.startsWith('javascript:')) n.removeAttribute(a.name);
    });
  });
}

/**
 * Le document du client — le même que le PDF : logo, couleurs, filigrane,
 * lignes, totaux, photos —, à la taille de son écran. Le PDF est une feuille
 * A4 qui s'affiche en tout petit sur un téléphone ; ici, la page s'adapte au
 * téléphone, à la tablette et à l'ordinateur.
 *
 * <p>La page est posée dans une ombre (shadow DOM) : ses styles ne touchent
 * pas la page du lien, ni l'inverse.
 *
 * @param {object} props
 * @param {string} props.jeton jeton porté par le lien
 * @param {(pret: boolean) => void} [props.onPret] dit si le document s'affiche
 * @param {import('react').ReactNode} [props.children] posé entre le document et
 *   l'annexe photos : le bulletin à scanner
 * @returns {JSX.Element|null} le document, ou rien s'il n'est pas disponible
 */
export function FeuilleDocument({ jeton, onPret, children }) {
  const hote = useRef(null);
  const hoteAnnexe = useRef(null);
  const [html, setHtml] = useState(null);

  useEffect(() => {
    let actif = true;
    if (typeof pagesPubliquesApi.feuille !== 'function') return undefined;
    Promise.resolve()
      .then(() => pagesPubliquesApi.feuille(jeton))
      .then((h) => {
        if (actif) setHtml(h || null);
      })
      .catch(() => {});
    return () => {
      actif = false;
    };
  }, [jeton]);

  useEffect(() => {
    if (!html || !hote.current) {
      if (onPret) onPret(false);
      return;
    }
    const page = new DOMParser().parseFromString(html, 'text/html');
    const styles = [...page.querySelectorAll('style')].map((s) => s.textContent).join('\n');
    nettoie(page);
    // Le document d'abord, puis ce que la page pose entre les deux — le
    // bulletin à scanner —, puis l'annexe photos : comme dans le PDF.
    const annexe = [...page.body.querySelectorAll('.rendu-annexe')];
    annexe.forEach((n) => n.remove());
    const pose = (cible, noeuds) => {
      if (!cible) return;
      const racine = cible.shadowRoot || cible.attachShadow({ mode: 'open' });
      const style = document.createElement('style');
      style.textContent = styles.replace(/\bbody\b/g, ':host');
      racine.replaceChildren(style, ...noeuds.map((n) => document.importNode(n, true)));
    };
    pose(hote.current, [...page.body.childNodes]);
    if (annexe.length) {
      const enveloppe = document.createElement('div');
      enveloppe.className = 'page-web';
      annexe.forEach((n) => enveloppe.appendChild(n));
      pose(hoteAnnexe.current, [enveloppe]);
    }

    if (onPret) onPret(true);
  }, [html, onPret]);

  // Une photo touchée sur la page s'ouvre en grand, dans la même fenêtre :
  // on le note, pour que « Fermer » revienne en arrière au lieu d'empiler
  // la page une seconde fois — sinon « Retour » rouvrait la photo.
  useEffect(() => {
    const note = (e) => {
      const lien = (e.composedPath ? e.composedPath() : [e.target]).find(
        (n) => n && n.tagName === 'A' && /\/photo\/\d+/.test(n.getAttribute?.('href') || ''),
      );
      if (!lien) return;
      e.preventDefault();
      try {
        window.sessionStorage.setItem(CLE_PHOTO_DEPUIS_LA_PAGE, jeton);
      } catch {
        /* sans mémoire de session, « Fermer » mène à la page */
      }
      window.location.assign(lien.getAttribute('href'));
    };
    const cibles = [hote.current, hoteAnnexe.current].filter(Boolean);
    cibles.forEach((c) => c.addEventListener('click', note));
    return () => cibles.forEach((c) => c.removeEventListener('click', note));
  }, [html, jeton]);

  if (!html) return null;
  return (
    <>
      <section
        aria-label="Document"
        style={{ margin: '0 -14px 14px', overflowX: 'hidden' }}
        ref={hote}
        data-testid="feuille-document"
      />
      {children}
      <section
        aria-label="Photos"
        style={{ margin: '0 -14px 14px', overflowX: 'hidden' }}
        ref={hoteAnnexe}
        data-testid="feuille-annexe"
      />
    </>
  );
}
